// Ported from design_ideas/lernwelt/bundle.jsx — numeric keypad for direct-answer math

interface Props {
  value: string;
  setValue: (fn: (prev: string) => string) => void;
  submitted: boolean;
  isCorrect: boolean | null;
  disabled: boolean;
  /** Correct answer, known only after the server has graded. */
  answer: string;
  onEnter?: () => void;
  canSubmit?: boolean;
}

const KEYS = ['1','2','3','4','5','6','7','8','9','⌫','0','↵'];

export default function Keypad({ value, setValue, submitted, isCorrect, disabled, answer, onEnter, canSubmit = false }: Props) {
  // Stay neutral while the answer is being graded (isCorrect === null).
  const graded = submitted && isCorrect !== null;
  const enterActive = !!onEnter && canSubmit && !submitted && !disabled;
  const press = (k: string) => {
    if (submitted || disabled) return;
    if (k === '⌫') setValue(v => v.slice(0, -1));
    else if (k === '↵') { if (enterActive) onEnter!(); return; }
    else if (value.length < 6) setValue(v => v + k);
  };

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '1.1fr 1fr',
      gap: 18,
      marginBottom: 20,
      alignItems: 'stretch',
    }}>
      {/* display */}
      <div style={{
        background: graded
          ? (isCorrect ? 'var(--correct-soft)' : 'var(--wrong-soft)')
          : 'var(--card)',
        border: graded
          ? `2px solid ${isCorrect ? 'var(--correct)' : 'var(--wrong)'}`
          : '2px solid rgba(60,50,40,0.15)',
        borderRadius: 4,
        padding: '20px 24px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        opacity: disabled ? 0.4 : 1,
      }}>
        <div style={{
          fontFamily: 'var(--mono)',
          fontSize: 10, letterSpacing: '0.18em',
          color: 'var(--ink-muted)',
          textTransform: 'uppercase',
        }}>Deine Antwort</div>
        <div style={{
          fontFamily: 'var(--display)',
          fontSize: 72,
          fontWeight: 600,
          lineHeight: 1,
          color: graded
            ? (isCorrect ? 'var(--correct)' : 'var(--wrong)')
            : 'var(--ink)',
          textAlign: 'center',
        }}>
          {value || <span style={{ opacity: 0.25 }}>_ _</span>}
        </div>
        {isCorrect === false && answer && (
          <div role="status" aria-live="assertive" style={{
            fontFamily: 'var(--mono)',
            fontSize: 11, letterSpacing: '0.1em',
            color: 'var(--wrong)',
            textAlign: 'center',
          }}>RICHTIG: {answer}</div>
        )}
        {isCorrect === true && (
          <div role="status" aria-live="assertive" style={{
            fontFamily: 'var(--mono)',
            fontSize: 11, letterSpacing: '0.1em',
            color: 'var(--correct)',
            textAlign: 'center',
          }}>RICHTIG GERECHNET ✓</div>
        )}
        {!submitted && <div style={{ height: 11 }} />}
      </div>

      {/* pad */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 6,
      }}>
        {KEYS.map((k, i) => {
          const isEnter = k === '↵';
          const isBack = k === '⌫';
          const keyDisabled = submitted || disabled || (isEnter && !enterActive);
          const ariaLabel = isEnter ? 'Absenden' : isBack ? 'Löschen' : `Ziffer ${k}`;
          return (
            <button
              key={i}
              aria-label={ariaLabel}
              onClick={() => press(k)}
              disabled={keyDisabled}
              style={{
                minWidth: 44, minHeight: 44,
                padding: '10px 0',
                background: isEnter ? 'var(--ink)' : 'var(--card)',
                border: '1px solid rgba(60,50,40,0.18)',
                borderRadius: 3,
                fontFamily: 'var(--display)',
                fontSize: 20,
                fontWeight: 600,
                color: isEnter ? 'var(--parchment)' : isBack ? 'var(--ink-muted)' : 'var(--ink)',
                cursor: keyDisabled ? 'default' : 'pointer',
                opacity: keyDisabled ? 0.5 : 1,
                transition: 'all 0.1s',
              }}
              onMouseDown={e => { if (!keyDisabled) (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(1px)'; }}
              onMouseUp={e => { (e.currentTarget as HTMLButtonElement).style.transform = ''; }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.transform = ''; }}
            >{k}</button>
          );
        })}
      </div>
    </div>
  );
}
